import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lightbulb, Headset, Leaf, Droplets, Activity, Sprout,
  TrendingUp, Pill, ShieldAlert, Layers, Wind, Waves, Utensils, FlaskConical,
  CheckCircle2, Boxes, Target, Award, Beaker,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import WaveDivider from "@/components/WaveDivider";
import ProductCard from "@/components/ProductCard";
import ImageWithFallback from "@/components/ImageWithFallback";
import { getProducts } from "@/lib/api";
import { BRAND, IMAGES } from "@/lib/brand";
import { useSeo } from "@/lib/useSeo";

const RANGE = [
  { name: "Aquaculture Minerals", desc: "Macro & trace mineral solutions", icon: FlaskConical, cat: "minerals" },
  { name: "Pond Probiotics", desc: "Microbial and water-quality management", icon: Droplets, cat: "probiotics" },
  { name: "Gut Probiotics", desc: "Supports beneficial gut microbial balance", icon: Activity, cat: "gut-health" },
  { name: "Digestive Solutions", desc: "Supports digestion and nutrient utilization", icon: Beaker, cat: "gut-health" },
  { name: "Growth Support", desc: "Supports growth and feed performance", icon: TrendingUp, cat: "growth" },
  { name: "Vitamin & Nutritional", desc: "Functional nutritional support", icon: Pill, cat: "vitamins" },
  { name: "Stress Management", desc: "Supports performance in tough conditions", icon: ShieldAlert, cat: "stress" },
  { name: "Pond Bottom Management", desc: "Organic-load & compound management", icon: Layers, cat: "pond-management" },
  { name: "Toxic-Gas Management", desc: "Supports healthier pond conditions", icon: Wind, cat: "pond-management" },
  { name: "Water Quality Solutions", desc: "Stable and productive pond environments", icon: Waves, cat: "water-quality" },
  { name: "Feed & Functional", desc: "Supports efficient feed utilization", icon: Utensils, cat: "nutrition" },
  { name: "Technical Services", desc: "Guidance by pond, species & culture stage", icon: Headset, cat: "technical-services" },
];

const TRUST = [
  { title: "Quality", desc: "Consistent, application-oriented products", icon: ShieldCheck },
  { title: "Innovation", desc: "Modern aquaculture solutions", icon: Lightbulb },
  { title: "Technical Support", desc: "Practical pond-focused guidance", icon: Headset },
  { title: "Responsible Aquaculture", desc: "Designed for sustainable culture practices", icon: Leaf },
];

const WHY = [
  { title: "Integrated Solutions", desc: "Multiple aquaculture requirements addressed through one solution portfolio.", icon: Boxes },
  { title: "Application-Oriented", desc: "Products designed around practical pond-management requirements.", icon: Target },
  { title: "Technical Guidance", desc: "Support based on pond condition and culture requirements.", icon: Headset },
  { title: "Quality Focus", desc: "Consistent and reliable product approach.", icon: ShieldCheck },
  { title: "Innovation", desc: "Modern product-development and aquaculture thinking.", icon: Lightbulb },
  { title: "Responsible Aquaculture", desc: "Focused on practical and responsible culture management.", icon: Leaf },
];

const FLOW = [
  "Pond Assessment", "Water & Pond Condition", "Culture Stage", "Identify Requirement",
  "Select BIOSYN Solution", "Application Guidance", "Monitor Culture Performance",
];

const APPROACH_NODES = ["Gut Health", "Nutrition", "Minerals", "Water Quality", "Pond Bottom", "Stress Management"];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useSeo({
    title: "BIOSYN | Advanced Aquaculture Solutions | Shrimp & Fish Farming",
    description: "BIOSYN provides advanced aquaculture solutions including minerals, probiotics, gut health, nutrition, vitamin products, pond management and technical support for modern shrimp and fish farming.",
    image: IMAGES.heroPond,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "BIOSYN",
      description: "Advanced Aquaculture Solutions for shrimp and fish farming.",
      email: BRAND.email,
      telephone: `+${BRAND.phoneIntl}`,
      address: { "@type": "PostalAddress", addressLocality: "Belagavi", addressRegion: "Karnataka", postalCode: "590015", addressCountry: "IN" },
    },
  });

  useEffect(() => {
    getProducts({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "#063B63" }}>
        <div className="absolute inset-0">
          <ImageWithFallback src={IMAGES.heroPond} alt="Aquaculture pond with paddlewheel aerators for modern shrimp and fish farming" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, rgba(6,59,99,0.94) 0%, rgba(6,59,99,0.82) 42%, rgba(8,127,191,0.55) 100%)" }} />
        </div>
        <div className="container-x relative py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl reveal">
            <p className="overline-label mb-4" style={{ color: "#16B8D4" }}>Advanced Products • Smarter Pond Management</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Advanced Aquaculture Solutions
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-semibold" style={{ color: "#16B8D4" }}>
              Complete Solutions for Modern Shrimp &amp; Fish Farming
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>
              BIOSYN provides integrated aquaculture products, nutritional solutions, mineral solutions, probiotics and
              pond-management technologies backed by practical technical guidance.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/products" data-testid="hero-explore-btn" className="btn-green">
                Explore Our Products <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" data-testid="hero-expert-btn" className="btn-outline">
                Talk to a Technical Expert
              </Link>
            </div>
            <p className="mt-8 text-sm font-medium tracking-wide" style={{ color: "rgba(255,255,255,0.72)" }}>
              Shrimp • Fish • Minerals • Probiotics • Gut Health • Nutrition • Pond Management
            </p>
          </div>
        </div>
        {/* animated waves */}
        <div className="absolute bottom-0 left-0 right-0 leading-[0]">
          <div className="relative h-[70px] md:h-[90px]">
            <svg className="absolute bottom-0 w-[200%] h-full wave-anim-slow" viewBox="0 0 2880 120" preserveAspectRatio="none">
              <path fill="rgba(245,250,252,0.4)" d="M0,64L120,58.7C240,53,480,43,720,48C960,53,1200,75,1440,74.7C1680,75,1920,53,2160,48C2400,43,2640,53,2760,64L2880,75L2880,120L0,120Z" />
            </svg>
            <svg className="absolute bottom-0 w-[200%] h-full wave-anim" viewBox="0 0 2880 120" preserveAspectRatio="none">
              <path fill="#F5FAFC" d="M0,80L120,74.7C240,69,480,59,720,64C960,69,1200,91,1440,90.7C1680,91,1920,69,2160,64C2400,59,2640,69,2760,74.7L2880,80L2880,120L0,120Z" />
            </svg>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="container-x -mt-6 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {TRUST.map((t) => (
            <div key={t.title} data-testid={`trust-${t.title.toLowerCase().replace(/\s/g, "-")}`} className="biosyn-card p-5 md:p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: "rgba(22,184,212,0.12)" }}>
                <t.icon className="w-6 h-6" style={{ color: "#087FBF" }} />
              </div>
              <h3 className="font-heading font-bold text-base" style={{ color: "#063B63" }}>{t.title}</h3>
              <p className="mt-1 text-sm" style={{ color: "#4A6B82" }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="section container-x">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden biosyn-card !p-0">
              <ImageWithFallback src={IMAGES.aerialPonds} alt="Aerial view of modern shrimp and fish farming ponds" className="w-full h-[340px] md:h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:right-6 biosyn-card px-5 py-4 hidden sm:block">
              <p className="font-heading text-2xl font-extrabold" style={{ color: "#3FAE35" }}>Integrated</p>
              <p className="text-xs font-semibold" style={{ color: "#4A6B82" }}>Aquaculture Solutions</p>
            </div>
          </div>
          <div>
            <SectionHeading overline="About BIOSYN" title="Complete Solutions for Modern Shrimp & Fish Farming" />
            <p className="mt-5 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              BIOSYN provides a comprehensive range of aquaculture products, mineral solutions, probiotics, gut-health
              products, nutritional supplements, growth-support solutions and pond-management products, backed by practical
              technical guidance.
            </p>
            <p className="mt-4 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              We aim to provide integrated aquaculture solutions rather than isolated products—combining product technology
              with practical application support for farmers, dealers and aquaculture professionals.
            </p>
            <Link to="/about" data-testid="about-learn-more" className="btn-primary mt-7">
              Learn More About BIOSYN <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* COMPLETE RANGE */}
      <section className="section grain" style={{ background: "#FFFFFF" }}>
        <div className="container-x">
          <SectionHeading align="center" overline="Product Portfolio" title="Our Complete Aquaculture Range"
            subtitle="Integrated solutions for healthier ponds, better culture management and improved farm performance." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {RANGE.map((r) => {
              const to = r.cat === "technical-services" ? "/technical-services" : `/products?category=${r.cat}`;
              return (
                <Link key={r.name} to={to} data-testid={`range-${r.cat}`} className="biosyn-card p-6 group flex flex-col">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors" style={{ background: "rgba(8,127,191,0.09)" }}>
                    <r.icon className="w-6 h-6" style={{ color: "#087FBF" }} />
                  </div>
                  <h3 className="font-heading font-bold text-lg leading-snug" style={{ color: "#063B63" }}>{r.name}</h3>
                  <p className="mt-2 text-sm flex-1" style={{ color: "#4A6B82" }}>{r.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold transition-transform group-hover:translate-x-1" style={{ color: "#3FAE35" }}>
                    View Products <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section container-x">
        <SectionHeading align="center" overline="Signature Range" title="Featured BIOSYN Products"
          subtitle="Our flagship, application-oriented solutions for intensive shrimp and fish culture." />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
        <div className="text-center mt-10">
          <Link to="/products" className="btn-outline-blue" data-testid="featured-view-all">View All Products <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* OUR APPROACH TEASER */}
      <section className="section" style={{ background: "#063B63" }}>
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading light overline="Our Integrated Approach" title="One Portfolio. Every Pond Requirement." />
            <p className="mt-5 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>
              We aim to provide integrated aquaculture solutions rather than isolated products—combining product technology
              with practical application support for farmers, dealers and aquaculture professionals.
            </p>
            <Link to="/our-approach" data-testid="approach-explore" className="btn-green mt-7">
              Explore Our Approach <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative flex items-center justify-center h-[360px] md:h-[420px]">
            <div className="relative w-[300px] h-[300px] md:w-[360px] md:h-[360px]">
              <div className="absolute inset-0 rounded-full border float-y flex items-center justify-center text-center" style={{ borderColor: "rgba(22,184,212,0.35)", background: "rgba(8,127,191,0.15)" }}>
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full flex flex-col items-center justify-center text-center p-3" style={{ background: "#16B8D4" }}>
                  <span className="font-heading font-extrabold text-white text-lg leading-none">BIOSYN</span>
                  <span className="text-[10px] mt-1 font-semibold text-white/90 leading-tight">Integrated Aquaculture Solutions</span>
                </div>
              </div>
              {APPROACH_NODES.map((n, i) => {
                const angle = (i / APPROACH_NODES.length) * 2 * Math.PI - Math.PI / 2;
                const r = 46;
                const x = 50 + r * Math.cos(angle);
                const y = 50 + r * Math.sin(angle);
                return (
                  <div key={n} className="absolute -translate-x-1/2 -translate-y-1/2 px-3 py-2 rounded-xl text-[11px] md:text-xs font-semibold text-center whitespace-nowrap shadow-lg"
                    style={{ left: `${x}%`, top: `${y}%`, background: "#FFFFFF", color: "#063B63" }}>
                    {n}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION FLOW */}
      <section className="section container-x">
        <SectionHeading align="center" overline="How It Works" title="From Pond Condition to Practical Solution"
          subtitle="A structured approach that differentiates BIOSYN from companies that simply sell individual products." />
        <div className="mt-12 grid gap-3 md:grid-cols-4 lg:grid-cols-7">
          {FLOW.map((step, i) => (
            <div key={step} className="relative">
              <div className="biosyn-card p-4 h-full flex flex-col items-center text-center">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mb-2" style={{ background: i === FLOW.length - 1 ? "#3FAE35" : "#087FBF" }}>{i + 1}</span>
                <p className="text-xs font-semibold leading-snug" style={{ color: "#17324D" }}>{step}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY BIOSYN */}
      <section className="section grain" style={{ background: "#FFFFFF" }}>
        <div className="container-x">
          <SectionHeading align="center" overline="The BIOSYN Difference" title="Why BIOSYN" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="biosyn-card p-7">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(63,174,53,0.12)" }}>
                  <w.icon className="w-6 h-6" style={{ color: "#197A35" }} />
                </div>
                <h3 className="font-heading font-bold text-lg" style={{ color: "#063B63" }}>{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4A6B82" }}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="section" style={{ background: "#F5FAFC" }}>
        <div className="container-x text-center">
          <SectionHeading align="center" overline="Our Commitment" title="Built On Four Principles" />
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-4">
            {["Quality", "Innovation", "Technical Support", "Responsible Aquaculture"].map((w) => (
              <span key={w} className="font-heading text-2xl md:text-4xl font-extrabold tracking-tight" style={{ color: "#087FBF" }}>{w}</span>
            ))}
          </div>
          <p className="mt-8 max-w-3xl mx-auto text-base leading-relaxed" style={{ color: "#4A6B82" }}>
            Our focus is on developing and supplying practical, consistent and application-oriented solutions for modern
            intensive aquaculture.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" style={{ background: "#063B63" }}>
        <ImageWithFallback src={IMAGES.aeration} alt="Aquaculture pond aeration" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="container-x relative py-20 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white max-w-4xl mx-auto leading-tight">
            Better Pond Management Starts With the Right Solution.
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg" style={{ color: "rgba(255,255,255,0.82)" }}>
            BIOSYN combines advanced aquaculture products with practical technical guidance to help farmers manage changing
            pond and culture conditions.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" data-testid="final-explore-btn" className="btn-green">Explore Products <ArrowRight className="w-5 h-5" /></Link>
            <Link to="/contact" data-testid="final-talk-btn" className="btn-outline">Talk to BIOSYN</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
